import React, { useState, useMemo } from 'react';
import { Doughnut, Bar } from 'react-chartjs-2'; // Assuming you use a charting library like react-chartjs-2
import { FaMoneyBillWave, FaRupeeSign, FaChartLine, FaUsers } from 'react-icons/fa';

// --- Reusable Components ---

// 1. Summary Card Component
const SummaryCard = ({ title, value, icon: Icon, color }) => (
  <div className={`p-4 rounded-xl shadow-lg transition duration-300 hover:shadow-xl ${color}`}>
    <div className="flex items-center justify-between">
      <div className="text-sm font-semibold text-gray-700">{title}</div>
      <Icon className="text-2xl text-gray-500" />
    </div>
    <div className="mt-2 text-3xl font-bold text-gray-900">
      {value}
    </div>
  </div>
);

// 2. Data Filtering Form Component (Small and Cool)
const DateFilterForm = ({ onFilterChange }) => {
  const [period, setPeriod] = useState('monthly');

  const handleChange = (e) => {
    setPeriod(e.target.value);
    onFilterChange(e.target.value);
  };

  return (
    <div className="bg-white p-4 rounded-xl shadow-md flex justify-between items-center mb-6">
      <h3 className="text-lg font-bold text-gray-700">Financial Period</h3>
      <div className="flex items-center space-x-4">
        <label htmlFor="period" className="text-gray-600">View By:</label>
        <select
          id="period"
          value={period}
          onChange={handleChange}
          className="p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out bg-gray-50"
        >
          <option value="monthly">Monthly</option>
          <option value="quarterly">Quarterly</option>
          <option value="yearly">Yearly</option>
        </select>
      </div>
    </div>
  );
};

// --- Dummy Data (Indian Context) ---
const mockFinancialData = {
  monthly: {
    total: 8500000, // ₹85 Lakhs
    payroll: 6500000,
    recruitment: 1200000,
    training: 800000,
    averageSalary: 72500,
  },
  yearly: {
    total: 102000000, // ₹10.2 Crore
    payroll: 78000000,
    recruitment: 14400000,
    training: 9600000,
    averageSalary: 75000,
  },
  // Add quarterly data for full functionality
};

// Function to format numbers into Indian Rupee Lakhs/Crores
const formatCurrency = (amount) => {
  if (amount >= 10000000) {
    return `₹${(amount / 10000000).toFixed(2)} Cr`;
  }
  if (amount >= 100000) {
    return `₹${(amount / 100000).toFixed(2)} Lakhs`;
  }
  return `₹${amount.toLocaleString('en-IN')}`;
};

// --- Main Dashboard Component ---

const FinanceDashboard = () => {
  const [filter, setFilter] = useState('monthly'); // 'monthly', 'quarterly', 'yearly'

  // Get data based on the current filter
  const currentData = useMemo(() => {
    return mockFinancialData[filter] || mockFinancialData.monthly;
  }, [filter]);

  // Data for the Doughnut Chart
  const budgetDistributionData = {
    labels: ['Payroll', 'Recruitment', 'Training'],
    datasets: [
      {
        data: [currentData.payroll, currentData.recruitment, currentData.training],
        backgroundColor: ['#4299E1', '#F6AD55', '#48BB78'], // Blue, Orange, Green
        hoverBackgroundColor: ['#3182CE', '#ED8936', '#38A169'],
        borderWidth: 0,
      },
    ],
  };

  // Data for the Bar Chart (Mock Monthly Trend)
  const monthlyExpenseTrend = {
    labels: ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep'],
    datasets: [
      {
        label: 'Total HR Expense (in Lakhs)',
        data: [8.0, 8.5, 7.9, 9.1, 8.8, 8.5].map(val => val * 100000), // Convert Lakhs to actual value
        backgroundColor: '#4299E1',
        hoverBackgroundColor: '#3182CE',
        borderRadius: 5,
      },
    ],
  };

  return (
    <div className="min-h-screen p-4 sm:p-6 md:p-8 bg-gray-50" style={{ backgroundColor: '#F3F4F6' }}>
      <h1 className="text-3xl font-extrabold text-gray-800 mb-6 border-b pb-2">HR Finance Dashboard 💰</h1>
      
      {/* Date Filter */}
      <DateFilterForm onFilterChange={setFilter} />

      {/* 1. Summary Cards Section - Responsive Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <SummaryCard
          title={`Total HR Spend (${filter.charAt(0).toUpperCase() + filter.slice(1)})`}
          value={formatCurrency(currentData.total)}
          icon={FaMoneyBillWave}
          color="bg-white"
        />
        <SummaryCard
          title="Payroll Cost"
          value={formatCurrency(currentData.payroll)}
          icon={FaRupeeSign}
          color="bg-white"
        />
        <SummaryCard
          title="Average Employee Salary"
          value={formatCurrency(currentData.averageSalary)}
          icon={FaUsers}
          color="bg-white"
        />
        <SummaryCard
          title="Recruitment Budget Used"
          value={formatCurrency(currentData.recruitment)}
          icon={FaChartLine}
          color="bg-white"
        />
      </div>

      {/* 2. Charts Section - Responsive Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        
        {/* Budget Distribution Chart (Doughnut) */}
        <div className="lg:col-span-1 bg-white p-6 rounded-xl shadow-lg flex flex-col items-center">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Budget Distribution (%)</h2>
          <div className="h-64 w-64">
             {/* Chart.js component placeholder */}
             <Doughnut data={budgetDistributionData} options={{ maintainAspectRatio: false }} />
          </div>
        </div>
        
        {/* Expense Trend Chart (Bar) */}
        <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-lg">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Monthly HR Expense Trend</h2>
          <div className="h-80">
            {/* Chart.js component placeholder */}
            <Bar data={monthlyExpenseTrend} options={{ maintainAspectRatio: false }} />
          </div>
        </div>
      </div>

      {/* 3. Detailed Expense Table */}
      <div className="bg-white p-6 rounded-xl shadow-lg">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Detailed Compensation Report (Sep 2025)</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Designation</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Base Salary</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Taxes/Deductions</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Net Payable</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {/* Dummy Indian Employees */}
              {[{ name: 'Arjun Reddy', desig: 'Project Lead', base: 120000, tax: 25000, net: 95000 },
                { name: 'Priya Sharma', desig: 'HR Manager', base: 90000, tax: 18000, net: 72000 },
                { name: 'Karan Singh', desig: 'Software Engineer', base: 75000, tax: 15000, net: 60000 },
              ].map((emp, index) => (
                <tr key={index} className="hover:bg-blue-50 transition duration-150">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{emp.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{emp.desig}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatCurrency(emp.base)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-red-500">{formatCurrency(emp.tax)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">{formatCurrency(emp.net)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination/View All button form would go here */}
      </div>
    </div>
  );
};

export default FinanceDashboard;